import { BaseService } from '@/infrastructure/api/base.service'
import type { IUserProfileService } from '@/domain/interfaces/IUserProfileService'
import type {
  UserProfile,
  UserProfileResponse,
  UserCredits,
  UserCreditsResponse,
  SubscriptionDetails,
} from '@/domain/entities/UserProfile'
import type {
  MemberProfileDetails,
  UpdateMemberProfilePayload,
} from '@/domain/entities/MemberProfile'

/** camelCase payload of /Subscription/GetSubscriptionDetails (anonymous object, not a model). */
interface SubscriptionDetailsResponse {
  isOrganic?: boolean
  subscriptionIsPaid?: boolean
  allowUpgrade?: boolean
  paymentFailed?: boolean
  currencyCode?: string
  isDefaultOrganicProduct?: boolean
}

export class UserProfileService extends BaseService implements IUserProfileService {
  constructor() {
    super('')
  }

  async getUserProfile(): Promise<UserProfile> {
    // API returns { success, information: { Email, ContactName, ... } } (PascalCase)
    const response = await this.get<UserProfileResponse>('/V2/User/GetUser')
    return response.information
  }

  async getCredits(): Promise<UserCredits> {
    // API returns { result, availableExportCredit, availableMobileCredit, availableSearch }
    const response = await this.get<UserCreditsResponse>('/V2/Export/GetAvailableCredit')
    return {
      availableExportCredit: response.availableExportCredit,
      availableMobileCredit: response.availableMobileCredit,
      availableSearchCredit: response.availableSearch,
    }
  }

  async getSubscriptionDetails(): Promise<SubscriptionDetails> {
    // Unlike most endpoints (PascalCase models), /Subscription/GetSubscriptionDetails
    // returns a camelCase anonymous object (isOrganic, subscriptionIsPaid, …). Reading
    // it as PascalCase leaves every flag undefined → the status always shows "On Trial"
    // and Activate/Cancel actions never appear. Map camelCase → the PascalCase shape here.
    const res = await this.get<SubscriptionDetailsResponse>('/Subscription/GetSubscriptionDetails')
    return {
      IsOrganic: res.isOrganic ?? false,
      SubscriptionIsPaid: res.subscriptionIsPaid ?? false,
      AllowUpgrade: res.allowUpgrade ?? false,
      PaymentFailed: res.paymentFailed ?? false,
      CurrencyCode: res.currencyCode ?? 'GBP',
      IsDefaultOrganicProduct: res.isDefaultOrganicProduct ?? false,
    }
  }

  async getMemberProfileDetails(): Promise<MemberProfileDetails> {
    return this.get<MemberProfileDetails>('/Manage/GetMemberProfile')
  }

  async updateMemberProfile(payload: UpdateMemberProfilePayload): Promise<void> {
    await this.post<void>('/Manage/UpdateMemberProfile', payload)
  }
}
